import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, List, X } from "@phosphor-icons/react";
import Logo from "../../components/Logo";
import { NAV_LINKS } from "./data";
import { scrollTo } from "./utils";

export function StickyNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goAnchor = (href) => (e) => {
    if (href.startsWith("#")) {
      e.preventDefault();
      setMobileOpen(false);
      scrollTo(href.slice(1));
    }
  };

  return (
    <header
      data-testid="landing-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0A0A0A]/90 backdrop-blur-md border-b border-white/10 shadow-[0_1px_0_rgba(255,255,255,0.02)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-3">
        <Link to="/" data-testid="landing-logo-link" className="min-w-0">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-7 text-sm">
          {NAV_LINKS.map((l) =>
            l.external ? (
              <Link
                key={l.label}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-neutral-300 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5A1F] rounded-sm"
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.label}
                href={l.to}
                onClick={goAnchor(l.to)}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-neutral-300 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5A1F] rounded-sm"
              >
                {l.label}
              </a>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/login"
            data-testid="nav-signin"
            className="text-neutral-300 hover:text-white text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5A1F] rounded-sm"
          >
            Sign In
          </Link>
          <Link
            to="/contact?intent=concierge-setup"
            data-testid="nav-primary-cta"
            className="btn-primary rounded-full px-4 py-2 text-sm inline-flex items-center gap-2 whitespace-nowrap"
          >
            Set It Up For Me — Free
            <ArrowRight size={14} weight="bold" />
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="lg:hidden text-white p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5A1F] rounded-md"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          data-testid="nav-mobile-toggle"
        >
          {mobileOpen ? <X size={22} /> : <List size={22} />}
        </button>
      </div>

      {mobileOpen && (
        <div
          className="lg:hidden border-t border-white/10 bg-[#0A0A0A]/95 backdrop-blur-md"
          data-testid="nav-mobile-menu"
        >
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-3 text-sm">
            {NAV_LINKS.map((l) =>
              l.external ? (
                <Link
                  key={l.label}
                  to={l.to}
                  onClick={() => setMobileOpen(false)}
                  className="text-neutral-300 hover:text-white py-2"
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.label}
                  href={l.to}
                  onClick={goAnchor(l.to)}
                  className="text-neutral-300 hover:text-white py-2"
                >
                  {l.label}
                </a>
              )
            )}
            <Link
              to="/login"
              onClick={() => setMobileOpen(false)}
              className="text-neutral-300 hover:text-white py-2"
            >
              Sign In
            </Link>
            <Link
              to="/contact?intent=concierge-setup"
              onClick={() => setMobileOpen(false)}
              className="btn-primary rounded-full px-4 py-3 text-sm inline-flex items-center justify-center gap-2 mt-2"
            >
              Set It Up For Me — Free
              <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
